import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ServiceBoxes from "../components/ServiceBoxes";
import FaqSection from "../components/FaqSection";

export default function YdelsePage() {
  return (
    <>
      <Helmet>
        <title>Behandlinger & Ydelser | Kita Akupunktur</title>
        <meta
          name="description"
          content="Få overblik over behandlinger hos Kita Akupunktur i Risskov: Klassisk akupunktur, japansk akupunktur, øreakupunktur og kostvejledning. Læs mere."
        />
        <meta property="og:title" content="Behandlinger & Ydelser | Kita Akupunktur" />
        <meta
          property="og:description"
          content="Få overblik over behandlinger hos Kita Akupunktur i Risskov: Klassisk akupunktur, japansk akupunktur, øreakupunktur og kostvejledning. Læs mere."
        />
        <link rel="canonical" href="https://kita-akupunktur.dk/ydelse/" />
      </Helmet>

      {/* Header */}
      <section className="bg-gradient-to-b from-[#FFFAF7] via-[#FFE0CF]/30 to-[#FFFAF7] py-14 lg:py-20 border-b border-[#ffe0cf]/80 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-20">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-[#593427]">
            Ydelser & Behandlinger
          </h1>
          <p className="text-base sm:text-lg text-[#593427]/80 mt-3 max-w-2xl">
            Har du brug for hjælp til akupunktur og velvære? Se vores behandlinger og ydelser hos{" "}
            <Link to="/" className="text-[#d74814] font-semibold underline decoration-[#ffe0cf] hover:text-[#b3360b] hover:decoration-[#d74814] transition-colors">
              Kita Akupunktur
            </Link>{" "}
            her.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <div className="py-16">
        <ServiceBoxes />
      </div>

      {/* Målrettede behandlingsområder */}
      <section className="pb-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="glass-sakura p-8 sm:p-10 rounded-3xl border border-[#ffe0cf] space-y-6">
          <div className="text-center max-w-2xl mx-auto space-y-2">
            <span className="text-xs uppercase tracking-wider font-semibold text-[#d74814] block">
              Målrettede indsatser
            </span>
            <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
              Særlige fokusområder i klinikken
            </h2>
            <p className="text-[#7a5b4f] text-sm sm:text-base leading-relaxed">
              Udover de primære behandlingsformer har vi specialiseret os i en lang række konkrete lidelser og kropslige ubalancer:
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-2">
            {[
              {
                title: "Stress & søvnløshed",
                desc: "Ro på nervesystemet & bedre nattesøvn",
                link: "/stress",
                icon: "fa-solid fa-moon",
              },
              {
                title: "Migræne & hovedpine",
                desc: "Lindring af spændingshovedpine & anfald",
                link: "/migraene",
                icon: "fa-solid fa-head-side-virus",
              },
              {
                title: "Ondt i kæben",
                desc: "Spændte tyggemuskler, kliklyde & tandpres",
                link: "/ondt-i-kaeben",
                icon: "fa-solid fa-teeth",
              },
              {
                title: "Spændinger i nakke & skuldre",
                desc: "Infiltrationer, myoser & stivhed",
                link: "/spaendinger",
                icon: "fa-solid fa-person-dots-from-line",
              },
              {
                title: "Ondt i ryggen & lænd",
                desc: "Hekseskud, hold i ryggen & iskiassmerter",
                link: "/ondt-i-ryggen",
                icon: "fa-solid fa-bone",
              },
              {
                title: "Led- og muskelsmerter",
                desc: "Slidgigt, overbelastning & frossen skulder",
                link: "/led-og-muskelsmerter",
                icon: "fa-solid fa-crutch",
              },
              {
                title: "Vægtregulering",
                desc: "Nålebehandling kombineret med kostvejledning",
                link: "/vaegtregulering",
                icon: "fa-solid fa-scale-balanced",
              },
              {
                title: "Rygestop behandling",
                desc: "Mindre nikotintrang & kropslig ro",
                link: "/rygestop-behandling",
                icon: "fa-solid fa-ban-smoking",
              },
            ].map((area, idx) => (
              <Link
                key={idx}
                to={area.link}
                className="p-4 rounded-2xl bg-white/80 hover:bg-white border border-[#ffe0cf] transition-all hover:-translate-y-0.5 hover:shadow-xs group flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center space-x-2 text-[#d74814] mb-2">
                    <i className={`${area.icon} text-sm`} aria-hidden="true" />
                    <span className="text-xs font-semibold uppercase tracking-wider">Behandling</span>
                  </div>
                  <h3 className="font-serif font-semibold text-[#593427] text-base group-hover:text-[#d74814] transition-colors">
                    {area.title}
                  </h3>
                  <p className="text-xs text-[#7a5b4f] mt-1 leading-relaxed">
                    {area.desc}
                  </p>
                </div>
                <div className="pt-3 mt-3 border-t border-[#ffe0cf]/60 flex items-center justify-between text-xs font-medium text-[#d74814]">
                  <span>Læs mere</span>
                  <i className="fa-solid fa-arrow-right text-[11px] group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                </div>
              </Link>
            ))}
          </div>

          <div className="pt-4 flex flex-wrap items-center justify-center gap-3 border-t border-[#ffe0cf]/70 text-xs sm:text-sm">
            <Link
              to="/priser"
              className="px-4 py-2 rounded-full bg-[#d74814] text-white font-medium hover:bg-[#593427] transition-colors shadow-2xs"
            >
              Se priser & tilskudsmuligheder
            </Link>
            <Link
              to="/om-mig"
              className="px-4 py-2 rounded-full bg-white text-[#593427] border border-[#ffe0cf] font-medium hover:bg-[#ffe0cf]/40 transition-colors"
            >
              Læs om Mayumi Kita Thiele
            </Link>
            <Link
              to="/kontakt"
              className="px-4 py-2 rounded-full bg-white text-[#593427] border border-[#ffe0cf] font-medium hover:bg-[#ffe0cf]/40 transition-colors"
            >
              Bestil tid eller kontakt klinikken
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section sidst på siden */}
      <section className="pb-16 border-t border-[#ffe0cf]/80">
        <FaqSection
          title="Ofte stillede spørgsmål om vores behandlinger"
          subtitle="Få svar på valg af behandlingsform, forløb, RAB-godkendelse og forsikringstilskud hos Kita Akupunktur i Risskov."
          faqs={[
            {
              question: "Hvilken behandlingsform passer bedst til mine gener og symptomer?",
              answer: (
                <>
                  Du behøver ikke selv at have besluttet den præcise behandlingsform på forhånd. Ved din første konsultation tager vi en grundig samtale om dine symptomer, hvorefter jeg foretager en nænsom puls- og tungediagnose. Ud fra dette sammensætter jeg den mest effektive kombination af f.eks.{" "}
                  <Link to="/japansk-akupunktur" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                    japansk akupunktur (meridianterapi)
                  </Link>
                  ,{" "}
                  <Link to="/akupunktur" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                    klassisk kropsakupunktur
                  </Link>{" "}
                  eller{" "}
                  <Link to="/oere-akupunktur" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                    øreakupunktur
                  </Link>{" "}
                  skræddersyet til dig.
                </>
              ),
            },
            {
              question: "Hvor mange behandlinger skal jeg typisk forvente i et forløb?",
              answer: (
                <>
                  Antallet af behandlinger afhænger af, om dit problem er akut opstået (f.eks. et pludseligt{" "}
                  <Link to="/ondt-i-ryggen" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                    hekseskud
                  </Link>{" "}
                  eller en overbelastning) eller en kronisk tilstand som{" "}
                  <Link to="/stress" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                    længerevarende stress
                  </Link>{" "}
                  eller{" "}
                  <Link to="/migraene" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                    migræne
                  </Link>
                  . Ved akutte problemer er 2-4 sessioner ofte tilstrækkeligt, mens kroniske tilstande typisk kræver 5-8 ugentlige behandlinger for at opnå en stabil og blivende balance.
                </>
              ),
            },
            {
              question: "Er Mayumi Kita Thiele autoriseret og RAB-godkendt akupunktør?",
              answer: (
                <>
                  Ja, jeg er Registreret Alternativ Behandler (RAB) inden for akupunktur og medlem af Praktiserende Akupunktører. RAB-titlen er din officielle garanti for en solid sundhedsfaglig uddannelse, høje etiske standarder og løbende efteruddannelse. Læs mere om min baggrund under{" "}
                  <Link to="/om-mig" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                    om mig
                  </Link>
                  .
                </>
              ),
            },
            {
              question: "Yder Sygeforsikring 'danmark' og sundhedsforsikringer tilskud til behandlingerne?",
              answer: (
                <>
                  Ja, som følge af min RAB-godkendelse giver Sygeforsikring “danmark” tilskud til akupunkturbehandlinger, forudsat at du har en forudgående lægelig diagnose på dit problem. Rigtig mange private sundhedsforsikringer (f.eks. Danica, PFA, Topdanmark m.fl.) dækker ligeledes udgifterne. Se takster og information under{" "}
                  <Link to="/priser" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                    priser og tilskud
                  </Link>
                  .
                </>
              ),
            },
            {
              question: "Hvordan forbereder jeg mig bedst til min første konsultation i klinikken?",
              answer: (
                <>
                  Det er en fordel at have spist et let måltid ca. en time inden din behandling, så du ikke møder op på helt tom eller overfyldt mave. Tag behageligt, løstsiddende tøj på, så det er nemt at komme til albuer, knæ og ryg. Har du praktiske spørgsmål om parkering eller tidsbestilling, kan du finde dem på vores{" "}
                  <Link to="/kontakt" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                    kontaktside
                  </Link>{" "}
                  eller i vores{" "}
                  <Link to="/faq" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                    FAQ
                  </Link>
                  .
                </>
              ),
            },
          ]}
        />
      </section>
    </>
  );
}